import { Component } from "@theme/component";

/**
 * @typedef {object} ReviewConsumer
 * @property {string} [displayName]
 * @property {string} [name]
 */

/**
 * @typedef {object} Review
 * @property {number} stars
 * @property {string} [content]
 * @property {string} [createdAt]
 * @property {ReviewConsumer} [consumer]
 */

/**
 * @typedef {object} NumberOfReviews
 * @property {number} total
 * @property {number} oneStar
 * @property {number} twoStars
 * @property {number} threeStars
 * @property {number} fourStars
 * @property {number} fiveStars
 */

/**
 * @typedef {object} SummaryData
 * @property {number} starsAverage
 * @property {NumberOfReviews} numberOfReviews
 */

/**
 * @typedef {{ fiveStars: number, fourStars: number, threeStars: number, twoStars: number, oneStar: number }} StarPercentages
 */

/**
 * Cache for review pages by SKU
 * @type {Map<string, Map<number, Review[]>>}
 */
const reviewsCache = new Map();

/**
 * Cache for review summaries by SKU
 * @type {Map<string, SummaryData>}
 */
const summaryCache = new Map();

/**
 * TrustPilot Reviews Web Component
 * Displays paginated TrustPilot reviews with caching
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [skeleton] - The skeleton loading container
 * @property {HTMLElement} [content] - The main content container
 * @property {HTMLElement} [starsAverage] - Star average display
 * @property {HTMLElement} [starsContainer] - Stars icon container
 * @property {HTMLElement} [totalReviews] - Total reviews count
 * @property {HTMLElement} [recommendationPercent] - Recommendation percentage
 * @property {HTMLElement} [mobileRecommendationPercent] - Mobile recommendation percentage
 * @property {HTMLElement} [mobileBreakdown] - Mobile breakdown toggle content
 * @property {HTMLElement} [toggleBreakdownBtn] - Mobile breakdown toggle button
 * @property {HTMLElement} [toggleText] - Toggle button text
 * @property {HTMLElement} [toggleIcon] - Toggle button icon
 * @property {HTMLElement[]} [starBars] - Star breakdown bars
 * @property {HTMLElement[]} [starPercents] - Star percentage labels
 * @property {HTMLElement[]} [mobileStarBars] - Mobile star breakdown bars
 * @property {HTMLElement[]} [mobileStarPercents] - Mobile star percentage labels
 * @property {HTMLElement} [emptyState] - Empty state message
 * @property {HTMLElement} [reviewsList] - Reviews list container
 * @property {HTMLElement[]} [reviewCards] - Review card containers
 * @property {HTMLElement} [pagination] - Pagination container
 * @property {HTMLButtonElement} [prevBtn] - Previous page button
 * @property {HTMLButtonElement} [nextBtn] - Next page button
 * @property {HTMLElement[]} [pageButtons] - Page number buttons
 *
 * @extends {Component<Refs>}
 */
class GrTrustpilotReviews extends Component {
  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #reviewsPerPage = 3;

  /** @type {AbortController | null} */
  #summaryFetchController = null;

  /** @type {AbortController | null} */
  #reviewsFetchController = null;

  /** @type {SummaryData | null} */
  #summaryData = null;

  /** @type {number} */
  #totalReviews = 0;

  /** @type {boolean} */
  #mobileBreakdownOpen = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#summaryFetchController?.abort();
    this.#reviewsFetchController?.abort();
  }

  get sku() {
    return this.dataset.sku || "";
  }

  get businessUnitId() {
    return this.dataset.businessUnitId || "";
  }

  get apiKey() {
    return this.dataset.apiKey || "";
  }

  get apiBaseUrl() {
    return (
      this.dataset.apiBaseUrl ||
      "https://api.trustpilot.com/v1/product-reviews/business-units"
    );
  }

  get #totalPages() {
    return Math.ceil(this.#totalReviews / this.#reviewsPerPage);
  }

  /**
   * Initialize the component
   */
  async #initialize() {
    if (!this.sku) {
      console.error("GrTrustpilotReviews: SKU is required");
      return;
    }

    if (!this.businessUnitId || !this.apiKey) {
      console.error(
        "GrTrustpilotReviews: TrustPilot Business Unit ID and API Key are required",
      );
      return;
    }

    // Set reviews per page from data attribute
    const reviewsPerPage = parseInt(this.dataset.reviewsPerPage || "3", 10);
    this.#reviewsPerPage = reviewsPerPage > 0 ? reviewsPerPage : 3;

    // Fetch summary and first page of reviews
    await Promise.all([this.#fetchSummary(), this.#fetchReviewPage(1)]);

    this.#render();
    this.#prefetchPages();
  }

  /**
   * Fetch review summary for the SKU
   */
  async #fetchSummary() {
    const cachedSummary = summaryCache.get(this.sku);
    if (cachedSummary) {
      this.#summaryData = cachedSummary;
      this.#totalReviews = cachedSummary.numberOfReviews?.total || 0;
      return;
    }

    this.#summaryFetchController?.abort();
    this.#summaryFetchController = new AbortController();

    try {
      const baseUrl = `${this.apiBaseUrl}/${this.businessUnitId}`;
      const [regularSummary, importedSummary] = await Promise.all([
        this.#fetchSummaryApi(`${baseUrl}?sku=${encodeURIComponent(this.sku)}`),
        this.#fetchSummaryApi(
          `${baseUrl}/imported-reviews-summaries?sku=${encodeURIComponent(this.sku)}`,
        ),
      ]);

      this.#summaryData = this.#combineSummaryData(
        regularSummary,
        importedSummary,
      );
      this.#totalReviews = this.#summaryData.numberOfReviews?.total || 0;

      summaryCache.set(this.sku, this.#summaryData);
    } catch (error) {
      console.error("Error fetching review summary:", error);
      this.#summaryData = null;
    }
  }

  /**
   * Combine regular and imported summary data
   * @param {*} regular
   * @param {*} imported
   * @returns {SummaryData}
   */
  #combineSummaryData(regular, imported) {
    const regularData = regular?.data || regular || {};
    const importedData = imported?.data || imported || {};

    const regularReviews = regularData.numberOfReviews || {
      total: 0,
      oneStar: 0,
      twoStars: 0,
      threeStars: 0,
      fourStars: 0,
      fiveStars: 0,
    };

    const importedReviews = importedData.numberOfReviews || {
      total: 0,
      oneStar: 0,
      twoStars: 0,
      threeStars: 0,
      fourStars: 0,
      fiveStars: 0,
    };

    const totalReviews =
      (regularReviews.total || 0) + (importedReviews.total || 0);

    const combinedReviews = {
      total: totalReviews,
      oneStar: (regularReviews.oneStar || 0) + (importedReviews.oneStar || 0),
      twoStars:
        (regularReviews.twoStars || 0) + (importedReviews.twoStars || 0),
      threeStars:
        (regularReviews.threeStars || 0) + (importedReviews.threeStars || 0),
      fourStars:
        (regularReviews.fourStars || 0) + (importedReviews.fourStars || 0),
      fiveStars:
        (regularReviews.fiveStars || 0) + (importedReviews.fiveStars || 0),
    };

    const regularAverage = regularData.starsAverage || 0;
    const importedAverage = importedData.starsAverage || 0;
    const regularTotal = regularReviews.total || 0;
    const importedTotal = importedReviews.total || 0;

    const starsAverage =
      totalReviews > 0
        ? Number(
            (
              (regularAverage * regularTotal +
                importedAverage * importedTotal) /
              totalReviews
            ).toFixed(1),
          )
        : 0;

    return {
      starsAverage,
      numberOfReviews: combinedReviews,
    };
  }

  /**
   * Fetch a specific page of reviews (with abort controller for direct page clicks)
   * @param {number} page
   * @param {boolean} useAbort - Whether to use abort controller
   * @returns {Promise<Review[]>}
   */
  async #fetchReviewPage(page, useAbort = false) {
    if (page < 1) return [];

    const skuCache = reviewsCache.get(this.sku);
    if (skuCache?.has(page)) {
      return skuCache.get(page) || [];
    }

    // Only abort previous fetch for direct page clicks
    if (useAbort) {
      this.#reviewsFetchController?.abort();
      this.#reviewsFetchController = new AbortController();
    }

    try {
      const url = `${this.apiBaseUrl}/${this.businessUnitId}/reviews?sku=${encodeURIComponent(this.sku)}&page=${page}&perPage=${this.#reviewsPerPage}`;
      const response = await this.#fetchReviewsApi(
        url,
        useAbort ? this.#reviewsFetchController?.signal : undefined,
      );
      const reviews =
        response?.productReviews || response?.data || response || [];

      if (!reviewsCache.has(this.sku)) {
        reviewsCache.set(this.sku, new Map());
      }

      reviewsCache.get(this.sku)?.set(page, reviews);
      return reviews;
    } catch (error) {
      // Don't log abort errors
      if (error instanceof Error && error.name === "AbortError") {
        return [];
      }
      console.error(`Error fetching review page ${page}:`, error);
      return [];
    }
  }

  /**
   * Fetch API helper for summary requests
   * @param {string} url
   * @returns {Promise<*>}
   */
  async #fetchSummaryApi(url) {
    const response = await fetch(url, {
      signal: this.#summaryFetchController?.signal,
      mode: "cors",
      headers: {
        Accept: "application/json",
        apikey: this.apiKey,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Fetch API helper for reviews requests
   * @param {string} url
   * @param {AbortSignal} [signal] - Optional abort signal
   * @returns {Promise<*>}
   */
  async #fetchReviewsApi(url, signal) {
    const response = await fetch(url, {
      signal,
      mode: "cors",
      headers: {
        Accept: "application/json",
        apikey: this.apiKey,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Prefetch adjacent pages
   */
  #prefetchPages() {
    const pagesToPrefetch = [
      this.#currentPage - 1,
      this.#currentPage + 1,
      this.#currentPage + 2,
    ].filter((page) => page >= 1 && page <= this.#totalPages);

    pagesToPrefetch.forEach((page) => {
      const skuCache = reviewsCache.get(this.sku);
      if (!skuCache?.has(page)) {
        this.#fetchReviewPage(page);
      }
    });
  }

  /**
   * Get reviews for current page from cache
   * @returns {Review[]}
   */
  #getCurrentPageReviews() {
    const skuCache = reviewsCache.get(this.sku);
    return skuCache?.get(this.#currentPage) || [];
  }

  /**
   * Handle page button click - reads page from data-page attribute
   * Uses abort controller to cancel previous requests on rapid clicking
   * @param {Event} event
   */
  async handlePageClick(event) {
    event?.preventDefault();
    const target = event?.target;
    if (!(target instanceof HTMLElement)) return;

    const button = target.closest("[data-page]");
    if (!(button instanceof HTMLElement)) return;

    const page = parseInt(button.dataset.page || "", 10);
    if (
      isNaN(page) ||
      page < 1 ||
      page > this.#totalPages ||
      page === this.#currentPage
    )
      return;

    // Fetch with abort controller for direct page clicks
    const skuCache = reviewsCache.get(this.sku);
    if (!skuCache?.has(page)) {
      const reviews = await this.#fetchReviewPage(page, true);
      // If fetch was aborted or failed, don't update UI
      if (reviews.length === 0 && !reviewsCache.get(this.sku)?.has(page)) {
        return;
      }
    }

    this.#currentPage = page;
    this.#updateReviews();
    this.#updatePagination();
    this.#prefetchPages();
  }

  /**
   * Handle page change - called by arrow navigation
   * Does not use abort controller
   * @param {number | string} data
   * @param {Event} [event]
   */
  async handlePageChange(data, event) {
    event?.preventDefault();
    const page = typeof data === "number" ? data : parseInt(String(data), 10);
    if (
      isNaN(page) ||
      page < 1 ||
      page > this.#totalPages ||
      page === this.#currentPage
    )
      return;

    // Fetch without abort for arrow navigation
    const skuCache = reviewsCache.get(this.sku);
    if (!skuCache?.has(page)) {
      await this.#fetchReviewPage(page, false);
    }

    this.#currentPage = page;
    this.#updateReviews();
    this.#updatePagination();
    this.#prefetchPages();
  }

  /**
   * Handle previous page - called via on:click
   * @param {Event} event
   */
  handlePrevPage(event) {
    event.preventDefault();
    if (this.#currentPage > 1) {
      this.handlePageChange(this.#currentPage - 1, event);
    }
  }

  /**
   * Handle next page - called via on:click
   * @param {Event} event
   */
  handleNextPage(event) {
    event.preventDefault();
    if (this.#currentPage < this.#totalPages) {
      this.handlePageChange(this.#currentPage + 1, event);
    }
  }

  /**
   * Toggle mobile breakdown - called via on:click
   * @param {Event} event
   */
  toggleMobileBreakdown(event) {
    event.preventDefault();
    this.#mobileBreakdownOpen = !this.#mobileBreakdownOpen;

    const { mobileBreakdown, toggleText, toggleIcon, toggleBreakdownBtn } =
      this.refs;

    if (mobileBreakdown) {
      mobileBreakdown.classList.toggle("gr-hidden", !this.#mobileBreakdownOpen);
    }

    if (toggleText && toggleBreakdownBtn) {
      const showText = toggleBreakdownBtn.dataset.showText || "Show breakdown";
      const hideText = toggleBreakdownBtn.dataset.hideText || "Hide breakdown";
      toggleText.textContent = this.#mobileBreakdownOpen ? hideText : showText;
    }

    if (toggleIcon) {
      toggleIcon.classList.toggle("rotate-180", this.#mobileBreakdownOpen);
    }
  }

  /**
   * Toggle review content expand - called via on:click
   * @param {number | string} data
   * @param {Event} [event]
   */
  toggleReviewContent(data, event) {
    event?.preventDefault();
    const index = typeof data === "number" ? data : parseInt(String(data), 10);
    const { reviewCards } = this.refs;

    if (!reviewCards || !reviewCards[index]) return;

    const card = reviewCards[index];
    const content = card.querySelector("[data-content]");
    const button = card.querySelector("[data-toggle-btn]");

    if (content && button) {
      const isExpanded = content.classList.contains("expanded");

      if (isExpanded) {
        content.classList.remove("expanded");
        content.classList.add("line-clamp-3");
        button.textContent = "show more";
      } else {
        content.classList.add("expanded");
        content.classList.remove("line-clamp-3");
        button.textContent = "show less";
      }
    }
  }

  /**
   * Calculate star percentages
   * @returns {Record<string, number>}
   */
  #calculateStarPercentages() {
    if (!this.#summaryData?.numberOfReviews || this.#totalReviews === 0) {
      return {
        fiveStars: 0,
        fourStars: 0,
        threeStars: 0,
        twoStars: 0,
        oneStar: 0,
      };
    }

    const reviews = this.#summaryData.numberOfReviews;
    return {
      fiveStars: Math.round((reviews.fiveStars / this.#totalReviews) * 100),
      fourStars: Math.round((reviews.fourStars / this.#totalReviews) * 100),
      threeStars: Math.round((reviews.threeStars / this.#totalReviews) * 100),
      twoStars: Math.round((reviews.twoStars / this.#totalReviews) * 100),
      oneStar: Math.round((reviews.oneStar / this.#totalReviews) * 100),
    };
  }

  /**
   * Calculate recommendation percentage (4+ stars)
   * @returns {number}
   */
  #calculateRecommendationPercentage() {
    if (!this.#summaryData?.numberOfReviews || this.#totalReviews === 0)
      return 0;

    const reviews = this.#summaryData.numberOfReviews;
    return Math.round(
      ((reviews.fourStars + reviews.fiveStars) / this.#totalReviews) * 100,
    );
  }

  /**
   * Format date string
   * @param {string} dateStr
   * @returns {string}
   */
  #formatDate(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  /**
   * Main render method - updates DOM with data
   */
  #render() {
    const { skeleton, content } = this.refs;

    if (skeleton) {
      skeleton.classList.add("gr-hidden");
    }

    if (content) {
      content.classList.remove("gr-hidden");
    }

    this.#updateSummary();
    this.#updateReviews();
    this.#updatePagination();
  }

  /**
   * Update summary section
   */
  #updateSummary() {
    const {
      starsAverage,
      starsContainer,
      totalReviews,
      recommendationPercent,
      mobileRecommendationPercent,
      starBars,
      starPercents,
      mobileStarBars,
      mobileStarPercents,
    } = this.refs;

    if (!this.#summaryData) return;

    if (starsAverage) {
      starsAverage.textContent = String(this.#summaryData.starsAverage);
    }

    if (starsContainer) {
      this.#updateStarsDisplay(starsContainer, this.#summaryData.starsAverage);
    }

    if (totalReviews) {
      const reviewsOnText = this.dataset.reviewsOnText || "Reviews on";
      totalReviews.textContent = `${this.#totalReviews} ${reviewsOnText}`;
    }

    const recommendPercent = this.#calculateRecommendationPercentage();
    if (recommendationPercent) {
      recommendationPercent.textContent = `${recommendPercent}%`;
    }
    if (mobileRecommendationPercent) {
      mobileRecommendationPercent.textContent = `${recommendPercent}%`;
    }

    const percentages = this.#calculateStarPercentages();
    const starOrder = [
      "fiveStars",
      "fourStars",
      "threeStars",
      "twoStars",
      "oneStar",
    ];

    // Update desktop breakdown
    if (starBars && starPercents) {
      starOrder.forEach((key, index) => {
        const percent = percentages[key] || 0;

        if (starBars[index]) {
          starBars[index].style.width = `${percent}%`;
          // Update parent progressbar aria-valuenow
          const progressBar = starBars[index].parentElement;
          if (
            progressBar &&
            progressBar.hasAttribute("role") &&
            progressBar.getAttribute("role") === "progressbar"
          ) {
            progressBar.setAttribute("aria-valuenow", percent.toString());
          }
        }

        if (starPercents[index]) {
          starPercents[index].textContent = `${percent}%`;
        }
      });
    }

    // Update mobile breakdown
    if (mobileStarBars && mobileStarPercents) {
      starOrder.forEach((key, index) => {
        const percent = percentages[key] || 0;

        if (mobileStarBars[index]) {
          mobileStarBars[index].style.width = `${percent}%`;
          // Update parent progressbar aria-valuenow
          const progressBar = mobileStarBars[index].parentElement;
          if (
            progressBar &&
            progressBar.hasAttribute("role") &&
            progressBar.getAttribute("role") === "progressbar"
          ) {
            progressBar.setAttribute("aria-valuenow", percent.toString());
          }
        }

        if (mobileStarPercents[index]) {
          mobileStarPercents[index].textContent = `${percent}%`;
        }
      });
    }
  }

  /**
   * Update stars display in a container
   * @param {HTMLElement} container
   * @param {number} rating
   */
  #updateStarsDisplay(container, rating) {
    const stars = container.querySelectorAll("[data-star]");
    const fullIcon = this.dataset.starFullIcon || "";
    const halfIcon = this.dataset.starHalfIcon || "";
    const emptyIcon = this.dataset.starEmptyIcon || "";

    stars.forEach((star, index) => {
      const starValue = index + 1;
      let iconSrc = emptyIcon;

      if (rating >= starValue) {
        iconSrc = fullIcon;
      } else if (rating >= starValue - 0.5) {
        iconSrc = halfIcon;
      }

      if (star instanceof HTMLImageElement) {
        star.src = iconSrc;
      }
    });
  }

  /**
   * Update reviews list
   */
  #updateReviews() {
    const { reviewCards, emptyState, reviewsList } = this.refs;
    const reviews = this.#getCurrentPageReviews();

    if (emptyState) {
      emptyState.classList.toggle("gr-hidden", this.#totalReviews > 0);
    }

    if (reviewsList) {
      reviewsList.classList.toggle("gr-hidden", this.#totalReviews === 0);
    }

    if (!reviewCards) return;

    reviewCards.forEach((card, index) => {
      /** @type {Review | undefined} */
      const review = reviews[index];

      if (!review) {
        card.classList.add("gr-hidden");
        return;
      }

      card.classList.remove("gr-hidden");

      const starsContainer = card.querySelector("[data-stars]");
      const author = card.querySelector("[data-author]");
      const date = card.querySelector("[data-date]");
      const content = card.querySelector("[data-content]");
      const toggleBtn = card.querySelector("[data-toggle-btn]");

      if (starsContainer instanceof HTMLElement) {
        this.#updateStarsDisplay(starsContainer, review.stars || 0);
      }

      if (author) {
        author.textContent =
          review.consumer?.displayName || review.consumer?.name || "Anonymous";
      }

      if (date) {
        date.textContent = `Reviewed ${this.#formatDate(review.createdAt || "")}`;
      }

      if (content) {
        content.textContent = review.content || "";
        content.classList.remove("expanded");
        content.classList.add("line-clamp-3");

        if (toggleBtn) {
          const shouldShowToggle = (review.content || "").length > 295;
          toggleBtn.classList.toggle("gr-hidden", !shouldShowToggle);
          toggleBtn.textContent = "show more";
        }
      }
    });
  }

  /**
   * Update pagination controls
   */
  #updatePagination() {
    const { prevBtn, nextBtn, pageButtons, pagination } = this.refs;

    if (pagination) {
      pagination.classList.toggle("gr-hidden", this.#totalPages <= 1);
    }

    if (prevBtn) {
      prevBtn.disabled = this.#currentPage === 1;
    }

    if (nextBtn) {
      nextBtn.disabled = this.#currentPage >= this.#totalPages;
    }

    if (pageButtons) {
      const pageItems = this.#getPageItems();

      pageButtons.forEach((btn, index) => {
        const pageBtn = /** @type {HTMLButtonElement} */ (btn);
        const pageItem = pageItems[index];

        if (pageItem === undefined) {
          pageBtn.classList.add("gr-hidden");
          return;
        }

        pageBtn.classList.remove("gr-hidden");

        if (typeof pageItem === "number") {
          pageBtn.textContent = String(pageItem);
          pageBtn.dataset.page = String(pageItem);
          pageBtn.disabled = pageItem === this.#currentPage;
          pageBtn.classList.toggle(
            "bg-night-900",
            pageItem === this.#currentPage,
          );
          pageBtn.classList.toggle(
            "text-white",
            pageItem === this.#currentPage,
          );
          pageBtn.classList.toggle(
            "bg-transparent",
            pageItem !== this.#currentPage,
          );
          pageBtn.classList.toggle(
            "text-black",
            pageItem !== this.#currentPage,
          );
        } else {
          pageBtn.textContent = pageItem;
          pageBtn.disabled = true;
          pageBtn.classList.add("bg-transparent", "text-black");
          pageBtn.classList.remove("bg-night-900", "text-white");
        }
      });
    }
  }

  /**
   * Get page items for pagination display
   * @returns {Array<number | string>}
   */
  #getPageItems() {
    const totalPages = this.#totalPages;
    const currentPage = this.#currentPage;

    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage < 4) {
      return [1, 2, 3, 4, "..."];
    }

    if (currentPage === totalPages - 1) {
      return [1, "...", currentPage, currentPage + 1];
    }

    if (currentPage === totalPages) {
      return [1, "...", currentPage - 1, currentPage];
    }

    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "..."];
  }
}

if (!customElements.get("gr-trustpilot-reviews")) {
  customElements.define("gr-trustpilot-reviews", GrTrustpilotReviews);
}
